import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Sitter, SitterService } from 'us-web-services';
import { fullName } from 'us-react-components/dist/utils/string';

import {
  buildSearchHref,
  hasNext,
  hasPrevious,
  indexOfSitter,
  readSitterSearchNav,
  SitterSearchNavState,
} from '../../Sitters/sitterSearchNav';
import { Button, ButtonType } from '../../../components/Button';

interface Props {
  sitter: Sitter | null;
  activeTab: 'summary' | 'reviews' | 'calendar' | 'friends';
  viewerUserId?: number;
}

// Mirrors Angular's sitter-profile-header + sitter-profile-navigation directives.
// Tab hrefs use plain <a> so the browser fully navigates (HAProxy picks the
// right React bundle by role cookie). The "Back to search | Previous | Next"
// nav hydrates from the sessionStorage list the Sitters page writes — matches
// the SitterObjectService behavior in Angular.
export const SitterProfileChrome = ({
  sitter,
  activeTab,
  viewerUserId,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const [nav, setNav] = useState<SitterSearchNavState | null>(null);

  useEffect(() => {
    setNav(readSitterSearchNav());
  }, []);

  if (!sitter?.id) return <></>;

  const sitterId = sitter.id;
  const isSelf = viewerUserId === sitterId;
  const q = typeof window !== 'undefined' ? window.location.search : '';

  const tabs: Array<{ key: 'summary' | 'reviews' | 'calendar' | 'friends'; href: string }> = [
    { key: 'summary', href: `/app/sitter/${sitterId}${q}` },
    { key: 'reviews', href: `/app/sitter/${sitterId}/reviews${q}` },
  ];
  if (!isSelf) {
    tabs.push({ key: 'calendar', href: `/app/sitter/${sitterId}/calendar${q}` });
  }
  tabs.push({ key: 'friends', href: `/app/sitter/${sitterId}/friends${q}` });

  const currentIndex = indexOfSitter(nav, sitterId);
  const showNav = !!nav && (currentIndex > -1 || !nav.firstPage || !nav.lastPage);

  const goTo = (sid: number): void => {
    const params = new URLSearchParams(q);
    navigate(`/sitter/${sid}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const onPrevious = async (): Promise<void> => {
    if (!nav || !hasPrevious(nav, currentIndex)) return;
    if (currentIndex > 0) {
      goTo(nav.ids[currentIndex - 1]);
      return;
    }
    // At first item on this page but earlier pages exist — fetch the previous
    // page and land on its last sitter. Mirrors SitterObjectService.getPrevious.
    const params = new URLSearchParams(nav.query);
    params.set('page', String(nav.page - 1));
    const resp = await SitterService.getByFilter({
      params: Object.fromEntries(params.entries()),
    });
    const data = resp.data?.data ?? [];
    if (data.length > 0 && data[data.length - 1].id) {
      goTo(data[data.length - 1].id as number);
    }
  };

  const onNext = async (): Promise<void> => {
    if (!nav || !hasNext(nav, currentIndex)) return;
    if (currentIndex > -1 && currentIndex < nav.ids.length - 1) {
      goTo(nav.ids[currentIndex + 1]);
      return;
    }
    const params = new URLSearchParams(nav.query);
    params.set('page', String(nav.page + 1));
    const resp = await SitterService.getByFilter({
      params: Object.fromEntries(params.entries()),
    });
    const data = resp.data?.data ?? [];
    if (data.length > 0 && data[0].id) {
      goTo(data[0].id as number);
    }
  };

  const prevEnabled = hasPrevious(nav, currentIndex);
  const nextEnabled = hasNext(nav, currentIndex);

  return (
    <div className='mb-6'>
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-2xl font-semibold'>{fullName(sitter)}</h1>
      </div>

      {showNav && (
        <div
          id='sitter-detail-nav'
          className='bg-sapphire-50 rounded p-3 mb-4 text-sm font-semibold flex flex-wrap items-center gap-x-2'
        >
          <a href={buildSearchHref(nav, sitterId)} className='text-sapphire underline'>
            {t('pages.sitterReviews.nav.backToSearch')}
          </a>
          <span className='text-neutral-500'>|</span>
          <Button
            buttonType={ButtonType.tertiary}
            onClick={onPrevious}
            disabled={!prevEnabled}
            className={`!bg-transparent !border-0 !p-0 !py-0 !min-w-0 !rounded-none !text-sm !font-semibold ${
              prevEnabled ? 'underline' : '!text-neutral-400'
            }`}
          >
            {t('pages.sitterReviews.nav.previousCaregiver')}
          </Button>
          <span className='text-neutral-500'>|</span>
          <Button
            buttonType={ButtonType.tertiary}
            onClick={onNext}
            disabled={!nextEnabled}
            className={`!bg-transparent !border-0 !p-0 !py-0 !min-w-0 !rounded-none !text-sm !font-semibold ${
              nextEnabled ? 'underline' : '!text-neutral-400'
            }`}
          >
            {t('pages.sitterReviews.nav.nextCaregiver')}
          </Button>
        </div>
      )}

      <ul id='profile-tabs' className='flex gap-6 border-b border-neutral-200 mb-5'>
        {tabs.map((tab) => (
          <a key={tab.key} href={tab.href}>
            <li
              className={`cursor-pointer pb-2 ${
                activeTab === tab.key
                  ? 'border-b-2 border-sapphire font-semibold text-sapphire'
                  : 'text-neutral-700'
              }`}
            >
              {t(`pages.sitterReviews.tabs.${tab.key}`)}
            </li>
          </a>
        ))}
      </ul>
    </div>
  );
};
